import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageHeader, Panel, Button, IconButton, Field, TextInput, Select, ComboBox, Banner, Spinner, DataTable, Tag, Checkbox,
  Dialog, ConfirmDialog, EmptyState, FieldSection, ActivityFeed, exportRows, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import GradeFormDialog from '../../components/GradeFormDialog.jsx';
import GemaraExamineEditDialog from '../../components/GemaraExamineEditDialog.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { downloadInboxZip } from '../../next/inboxDownload.js';
import { formatHebrewDate, formatHebrewDateTime } from '../../utils/hebrewDate';
import { OCCUPATION_OPTIONS, STUDY_FORMATS, isWorking, partnerLabel } from '../../utils/memberStudy.js';
import {
  Tabs, FormGrid, AudioPlayer, apiError, useServerTable,
} from '../../next/ui';

/**
 * כרטיס חבר — new design. Same endpoints and rules as MemberCard.
 * Like the turbo record card: the details column is always visible (read-only) and "עריכה" opens the form in a dialog;
 * tabs: activity log (/members/:id/activity — exams, registrations, calls to the wife, card changes),
 * registrations (mark examined / gemara selection), grades (+ grade dialog), the exams the member took
 * (/american-exams/attempts?memberId — deleting one lets them retake it), phone update requests (approve / reject),
 * and the member's "הודעות למערכת" recordings (matched by phone / phoneAlt to the caller's number).
 * A new member shows the form directly.
 */
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';
const PHONE_ALT_LABEL = IS_MISHTAMERET ? 'טלפון אשה' : 'טלפון נוסף';
const FIELD_LABEL = { firstName: 'שם פרטי', lastName: 'שם משפחה', fatherName: 'שם האב', fatherInLawName: 'שם השווער', city: 'ישוב', street: 'רחוב' };
const NEW_FORM = {
  firstName: '', lastName: '', fatherName: '', fatherInLawName: '', phone: '', phoneAlt: '', email: '', idNumber: '', city: '', street: '', buildingNumber: '', entrance: '', apartment: '',
  occupation: '', studyFormat: '', studyPartner: '', kolel: '', kolelRef: null, kolelMorning: '', kolelMorningRef: null, kolelAfternoon: '', kolelAfternoonRef: null, kolelEvening: '', kolelEveningRef: null,
  registered: '', role: 'member', status: 'active',
};
const PERSONAL = [['firstName', 'שם פרטי'], ['lastName', 'משפחה'], ['fatherName', 'אביו'], ['fatherInLawName', 'חתן'], ['phone', 'נייד', true], ['phoneAlt', PHONE_ALT_LABEL, true], ...(IS_MISHTAMERET ? [['email', 'מייל', true]] : []), ['idNumber', 'מ.ז.', true]];
const ADDRESS = [['city', 'ישוב'], ['street', 'רחוב'], ['buildingNumber', 'מספר בניין'], ['entrance', 'כניסה'], ['apartment', 'דירה']];
const ACTIVITY_ICON = { exam: 'book', registration: 'calendar', call: 'phone', card: 'user', message: 'inbox' };
const ACTIVITY_FILTERS = [
  { value: 'exam', label: 'מבחנים' },
  ...(IS_MISHTAMERET ? [] : [{ value: 'registration', label: 'הרשמות' }]),
  { value: 'call', label: 'שיחות' },
  { value: 'message', label: 'הודעות' },
  { value: 'card', label: 'כרטיס' },
];
const INBOX_STATUS = {
  new: { label: 'חדש', tone: 'primary', icon: 'sparkle' },
  played: { label: 'הושמע', tone: 'neutral', icon: 'play' },
  todo: { label: 'לטיפול', tone: 'warning', icon: 'flag' },
  done: { label: 'הטיפול הסתיים', tone: 'success', icon: 'check' },
};
const inboxKind = (r) => (String(r.title || '').includes('הנהלה') ? 'להנהלה' : 'למערכת');
const callerOf = (r) => String(r.createdBy || '').replace(/^ivr-/, '');
const SOURCE_LABEL = { ivr: 'טלפון', web: 'ווב', import: 'ייבוא', system: 'מערכת', admin: 'מנהל', test: 'בדיקה' };
const ATTEMPT_CHANNEL = {
  web: { label: 'פורטל', icon: 'link', tone: 'primary' },
  ivr: { label: 'טלפון', icon: 'phone', tone: 'success' },
  import: { label: 'ייבוא', icon: 'upload', tone: 'neutral' },
};
const attemptScore = (r) => (r.maxScore != null ? `${r.score ?? 0}/${r.maxScore}` : String(r.score ?? 0));
const timeOf = (d) => new Date(d).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
const toFeedItem = (it) => ({
  ...it,
  icon: ACTIVITY_ICON[it.type] || 'history',
  day: formatHebrewDate(it.at),
  time: timeOf(it.at),
  by: [SOURCE_LABEL[it.source], it.by].filter(Boolean).join(' · ') || undefined,
});
const kolelLabel = (o) => `${o?.name || ''}${o?.city ? ` (${o.city})` : ''}${o?.code != null && o?.code !== '' ? ` — ${o.code}` : ''}`;
const classifySchedule = (schedule) => {
  const s = (schedule || '').trim();
  if (s.includes('בוקר') || s.includes('לפנה') || s.includes('לפני')) return 'morning';
  if (s.includes('אחה') || s.includes('צהר')) return 'afternoon';
  if (s.includes('ערב')) return 'evening';
  return 'unknown';
};

export default function MemberCardNext({ recordId, onClose }) {
  const toast = useCkToast();
  const params = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canReadGrades = hasPermission('grades', 'read');
  const canDeleteAttempts = hasPermission('grades', 'delete');
  const canReadRegs = hasPermission('examRegistration', 'read');
  const canReadMessages = hasPermission('messages', 'read');
  const canUpdateMessages = hasPermission('messages', 'update');
  const id = recordId || params.id;
  const isNew = id === 'new';

  const [tab, setTab] = useState('feed');
  const [editOpen, setEditOpen] = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [member, setMember] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [kolelOptions, setKolelOptions] = useState([]);
  const [kolelLoadError, setKolelLoadError] = useState('');
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [regExamineDialogReg, setRegExamineDialogReg] = useState(null);
  const [regExamineSaving, setRegExamineSaving] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveValue, setApproveValue] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [inboxCount, setInboxCount] = useState(null);
  const [inboxDownloading, setInboxDownloading] = useState(false);
  const [attemptCount, setAttemptCount] = useState(null);
  const [deleteAttempt, setDeleteAttempt] = useState(null);
  const [deletingAttempt, setDeletingAttempt] = useState(false);

  const loadMember = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/members/${id}`);
      setMember(data);
      setForm(data);
    } catch {
      setError('שגיאה בטעינת נתוני חבר');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadActivity = useCallback(async () => {
    if (isNew) return;
    setActivityLoading(true);
    try {
      const { data } = await api.get(`/members/${id}/activity`);
      setActivityItems((data.data || []).map(toFeedItem));
    } catch {
      setActivityItems([]);
    } finally {
      setActivityLoading(false);
    }
  }, [id, isNew]);
  useEffect(() => { if (tab === 'feed') loadActivity(); }, [tab, loadActivity]);

  useEffect(() => {
    if (!id) return;
    setKolelLoadError('');
    if (!IS_MISHTAMERET) {
      api.get('/kolels', { params: { all: '1', status: 'active', pageSize: '200' } })
        .then(({ data }) => setKolelOptions(data.data || []))
        .catch((err) => { setKolelOptions([]); setKolelLoadError(err.response?.status === 403 ? 'אין הרשאה לטעינת רשימת כוללים — לא ניתן לבחור כולל' : 'שגיאה בטעינת רשימת כוללים'); });
    }
    if (!isNew) loadMember();
    else { setForm(NEW_FORM); setLoading(false); }
  }, [id, isNew, loadMember]);

  /* ── registrations / grades (server paging, only when their tab is open) ── */
  const regs = useServerTable(async (q) => {
    if (isNew || tab !== 'regs') return { rows: regs?.rows || [], total: regs?.total || 0 };
    const { data } = await api.get('/registrations', { params: { memberId: id, page: q.page, pageSize: q.pageSize } });
    return { rows: data.data || [], total: data.total };
  }, [id, tab]);
  const grades = useServerTable(async (q) => {
    if (isNew || tab !== 'grades') return { rows: grades?.rows || [], total: grades?.total || 0 };
    const { data } = await api.get('/grades', { params: { memberId: id, page: q.page, pageSize: q.pageSize } });
    return { rows: data.data || [], total: data.total };
  }, [id, tab]);

  /* ── exams the member took (portal / phone / import) ── */
  const attempts = useServerTable(async (q) => {
    if (isNew || tab !== 'attempts') return { rows: attempts?.rows || [], total: attempts?.total || 0 };
    const { data } = await api.get('/american-exams/attempts', { params: { memberId: id, page: q.page, pageSize: q.pageSize } });
    setAttemptCount(data.total);
    return { rows: data.data || [], total: data.total };
  }, [id, tab]);
  useEffect(() => {
    if (isNew || !canReadGrades) return;
    api.get('/american-exams/attempts', { params: { memberId: id, pageSize: 1 } })
      .then(({ data }) => setAttemptCount(data.total))
      .catch(() => setAttemptCount(null));
  }, [id, isNew, canReadGrades]);
  const confirmDeleteAttempt = async () => {
    if (!deleteAttempt?._id) return;
    setDeletingAttempt(true);
    try {
      await api.delete(`/american-exams/attempts/${deleteAttempt._id}`);
      setDeleteAttempt(null);
      toast.success('המבחן נמחק — ניתן להיבחן עליו שוב');
      attempts.reload();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקת המבחן'));
    } finally {
      setDeletingAttempt(false);
    }
  };

  /* ── "הודעות למערכת" left from this member's phones ── */
  const inbox = useServerTable(async (q) => {
    if (isNew || tab !== 'messages') return { rows: inbox?.rows || [], total: inbox?.total || 0 };
    const { data } = await api.get('/messages', { params: { inbox: 'ivr', memberId: id, page: q.page, pageSize: q.pageSize } });
    setInboxCount(data.total);
    return { rows: data.data || [], total: data.total };
  }, [id, tab]);
  useEffect(() => {
    if (isNew || !canReadMessages || !member) return;
    api.get('/messages', { params: { inbox: 'ivr', memberId: id, pageSize: 1 } })
      .then(({ data }) => setInboxCount(data.total))
      .catch(() => setInboxCount(null));
  }, [id, isNew, canReadMessages, member?.phone, member?.phoneAlt]); // eslint-disable-line react-hooks/exhaustive-deps
  const setInboxRowStatus = (rowId, inboxStatus) => inbox.setRows((rows) => rows.map((r) => (r._id === rowId ? { ...r, inboxStatus } : r)));
  const setInboxStatus = async (r, next) => {
    const prev = r.inboxStatus || 'new';
    if (next === prev) return;
    setInboxRowStatus(r._id, next);
    try {
      await api.patch(`/messages/${r._id}/inbox-status`, { status: next });
      toast.success(`סומן «${INBOX_STATUS[next].label}»`);
    } catch (e) {
      setInboxRowStatus(r._id, prev);
      toast.error(apiError(e, 'שגיאה בעדכון הסטטוס'));
    }
  };
  const downloadMemberInbox = async () => {
    setInboxDownloading(true);
    try {
      const { fileCount } = await downloadInboxZip({ memberId: id });
      toast.success(fileCount ? `הורדו ${fileCount} הקלטות` : 'הקובץ הורד');
      inbox.reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setInboxDownloading(false);
    }
  };

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      let saveForm;
      if (IS_OPEN_GEMARA) {
        const refId = form.kolelRef?._id || form.kolelRef || null;
        const name = form.kolelRef?.name || form.kolel || '';
        saveForm = { ...form, kolelRef: refId, kolel: name, kolelMorningRef: refId, kolelMorning: name, kolelAfternoonRef: null, kolelAfternoon: '', kolelEveningRef: null, kolelEvening: '' };
      } else {
        saveForm = {
          ...form,
          kolelMorningRef: form.kolelMorningRef?._id || form.kolelMorningRef || null,
          kolelMorning: form.kolelMorningRef?.name || form.kolelMorning || '',
          kolelAfternoonRef: form.kolelAfternoonRef?._id || form.kolelAfternoonRef || null,
          kolelAfternoon: form.kolelAfternoonRef?.name || form.kolelAfternoon || '',
          kolelEveningRef: form.kolelEveningRef?._id || form.kolelEveningRef || null,
          kolelEvening: form.kolelEveningRef?.name || form.kolelEvening || '',
        };
        const primary = (saveForm.kolelMorningRef && { ref: saveForm.kolelMorningRef, name: saveForm.kolelMorning })
          || (saveForm.kolelAfternoonRef && { ref: saveForm.kolelAfternoonRef, name: saveForm.kolelAfternoon })
          || (saveForm.kolelEveningRef && { ref: saveForm.kolelEveningRef, name: saveForm.kolelEvening }) || null;
        saveForm.kolelRef = primary?.ref || null;
        saveForm.kolel = primary?.name || '';
      }
      if (isNew) {
        const { data } = await api.post('/members', saveForm);
        toast.success('חבר נוצר בהצלחה');
        if (onClose) onClose('created'); else navigate(`/members/${data._id}`);
      } else {
        const { data } = await api.put(`/members/${id}`, { ...saveForm, version: member.version });
        setMember(data);
        setForm(data);
        setEditOpen(false);
        toast.success('נשמר בהצלחה');
        if (tab === 'feed') loadActivity();
      }
    } catch (e) {
      setError(e.response?.status === 409 ? 'התנגשות גרסאות — רענן ונסה שוב' : apiError(e, 'שגיאת שמירה'));
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!approveValue.trim()) { toast.error('יש להזין את הערך החדש'); return; }
    setApproveLoading(true);
    try {
      await api.post(`/members/${id}/pending-updates/${approveTarget._id}/resolve`, { action: 'approve', value: approveValue.trim() });
      setApproveTarget(null);
      toast.success('הבקשה אושרה והפרטים עודכנו');
      loadMember();
      loadActivity();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה באישור'));
    } finally {
      setApproveLoading(false);
    }
  };
  const reject = async () => {
    try {
      await api.post(`/members/${id}/pending-updates/${rejectTarget._id}/resolve`, { action: 'reject' });
      toast.success('הבקשה נדחתה');
      loadMember();
      loadActivity();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בדחייה'));
    } finally {
      setRejectTarget(null);
    }
  };

  const toggleExamined = async (reg) => {
    regs.setRows((prev) => prev.map((r) => (r._id === reg._id ? { ...r, examined: !reg.examined } : r)));
    try {
      const { data: updated } = await api.put(`/registrations/${reg._id}`, { examined: !reg.examined });
      regs.setRows((prev) => prev.map((r) => (r._id === updated._id ? { ...r, examined: updated.examined } : r)));
    } catch {
      regs.setRows((prev) => prev.map((r) => (r._id === reg._id ? { ...r, examined: reg.examined } : r)));
      toast.error('שגיאה בעדכון סימון נבחן');
    }
  };
  const saveGemaraExamine = async (gemaraSelection) => {
    if (!regExamineDialogReg) return;
    setRegExamineSaving(true);
    try {
      const { data: updated } = await api.put(`/registrations/${regExamineDialogReg._id}`, { gemaraSelection });
      regs.setRows((prev) => prev.map((r) => (r._id === updated._id ? { ...r, gemaraSelection: updated.gemaraSelection } : r)));
      setRegExamineDialogReg(null);
    } catch { /* dialog stays open */ } finally {
      setRegExamineSaving(false);
    }
  };

  const findKolel = (ref) => {
    if (!ref) return null;
    const kid = String(ref?._id || ref);
    return kolelOptions.find((k) => String(k._id) === kid) || (typeof ref === 'object' ? ref : null);
  };
  const kolelsForSlot = (slot, currentRef) => {
    const current = findKolel(currentRef);
    const list = kolelOptions.filter((k) => { const c = classifySchedule(k.schedule); return c === slot || c === 'unknown' || (k.schedule || '').includes('כל היום'); });
    return current && !list.some((k) => String(k._id) === String(current._id)) ? [current, ...list] : list;
  };
  const kolelPicker = (label, nameKey, refKey, options, value) => (
    <Field label={label} hint={form[nameKey] && !value ? `מהאקסל: ${form[nameKey]}` : undefined} key={refKey}>
      <ComboBox
        value={value}
        onChange={(v) => setForm((f) => ({ ...f, [refKey]: v || null, [nameKey]: v?.name || '' }))}
        options={options}
        getLabel={kolelLabel}
        getKey={(o) => o?._id}
        placeholder="חיפוש לפי שם, עיר או קוד…"
        emptyText={kolelLoadError || 'לא נמצא כולל'}
      />
    </Field>
  );

  const pendingItems = (member?.pendingUpdates || []).filter((u) => u.status === 'pending');
  const named = isNew ? form : member || {};
  const fullName = `${named.firstName || ''} ${named.lastName || ''}`.trim();
  const back = () => (onClose ? onClose() : navigate('/members'));

  if (loading) return <div className="ck-empty"><Spinner size={26} /></div>;

  const regColumns = [
    { key: 'code', label: 'קוד מבחן', getValue: (r) => (r.examId?.code != null ? String(r.examId.code) : '—'), render: (r) => (r.examId?.code != null ? <span className="nx-num">{r.examId.code}</span> : '—') },
    ...(IS_OPEN_GEMARA ? [{
      key: 'gemaraSelection',
      label: 'חומר',
      width: 300,
      getValue: (r) => (r.gemaraSelection || []).map((sel) => {
        const ranges = sel.ranges?.map((x) => `${toHebrewNumeral(x.from)}–${toHebrewNumeral(x.to)}`).join(', ');
        return `${sel.tractate}${ranges ? ` (${ranges})` : ''}${sel.examined ? ' ✔' : ''}`;
      }).join(' | ') || '—',
    }] : [
      { key: 'exam', label: 'מבחן', getValue: (r) => r.examId?.name || '—' },
      { key: 'examined', label: 'נבחן', type: 'bool', getValue: (r) => !!r.examined, render: (r) => (r.examined ? <Tag tone="success" icon="check">כן</Tag> : '—') },
    ]),
    { key: 'period', label: 'מועד', getValue: (r) => r.periodId?.name || '—' },
    { key: 'location', label: 'עיר בחינה', getValue: (r) => r.location || '—' },
    { key: 'status', label: 'סטטוס', getValue: (r) => (r.status === 'registered' ? 'רשום' : 'בוטל'), render: (r) => (r.status === 'registered' ? <Tag tone="success">רשום</Tag> : <Tag tone="danger">בוטל</Tag>) },
    { key: 'source', label: 'מקור', getValue: (r) => (r.source === 'ivr' ? 'טלפון' : 'ווב'), render: (r) => <Tag icon={r.source === 'ivr' ? 'phone' : 'grid'}>{r.source === 'ivr' ? 'טלפון' : 'ווב'}</Tag> },
    { key: 'registeredAt', label: 'תאריך', type: 'date', render: (r) => formatHebrewDate(r.registeredAt) || '—' },
  ];
  const gradeColumns = [
    { key: 'code', label: 'קוד מבחן', getValue: (g) => (g.examId?.code != null ? String(g.examId.code) : '—'), render: (g) => (g.examId?.code != null ? <span className="nx-num">{g.examId.code}</span> : '—') },
    { key: 'exam', label: 'מבחן', getValue: (g) => g.examId?.name || '—' },
    { key: 'period', label: 'מועד', getValue: (g) => g.examPeriodId?.name || '—' },
    { key: 'tractate', label: 'מסכת', getValue: (g) => g.tractate || '—' },
    { key: 'score', label: 'ציון', type: 'number', render: (g) => (g.score >= 90 ? <Tag tone="success">{g.score}</Tag> : g.score) },
    { key: 'source', label: 'מקור', getValue: (g) => (g.source === 'ivr' ? 'IVR' : g.source === 'import' ? 'ייבוא' : 'ווב') },
    { key: 'enteredAt', label: 'תאריך', type: 'date', render: (g) => formatHebrewDate(g.enteredAt) || '—' },
  ];

  const formPanels = (
    <>
      <Panel title="פרטים אישיים" icon="user">
        <FormGrid cols={3}>
          {PERSONAL.map(([key, label, ltr]) => (
            <Field key={key} label={label}><TextInput ltr={!!ltr} value={form[key] || ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} /></Field>
          ))}
          {!isNew ? (
            <Field label="קוד אישי"><TextInput readOnly ltr value={form.personalCode != null ? String(form.personalCode).padStart(4, '0') : 'לא הוקצה'} style={{ color: 'var(--ck-primary)', fontWeight: 700 }} /></Field>
          ) : null}
          <Field label="עיסוק">
            <Select
              value={form.occupation || ''}
              onChange={(v) => setForm((f) => ({ ...f, occupation: v }))}
              placeholder="ללא"
              options={[...OCCUPATION_OPTIONS, ...(form.occupation && !OCCUPATION_OPTIONS.includes(form.occupation) ? [form.occupation] : [])]}
            />
          </Field>
          {isWorking(form.occupation) ? (
            <Field label="חברותא / שיעור">
              <Select value={form.studyFormat || ''} onChange={(v) => setForm((f) => ({ ...f, studyFormat: v || '' }))} placeholder="לא צוין" options={STUDY_FORMATS} />
            </Field>
          ) : null}
          {isWorking(form.occupation) && form.studyFormat ? (
            <Field label={partnerLabel(form.studyFormat)}><TextInput value={form.studyPartner || ''} onChange={(e) => setForm((f) => ({ ...f, studyPartner: e.target.value }))} /></Field>
          ) : null}
        </FormGrid>
      </Panel>
      <Panel title="כתובת" icon="home">
        <FormGrid cols={4}>
          {ADDRESS.map(([key, label]) => (
            <Field key={key} label={label}><TextInput value={form[key] || ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} /></Field>
          ))}
        </FormGrid>
      </Panel>
      {!IS_MISHTAMERET ? (
        <Panel title={IS_OPEN_GEMARA ? 'כולל' : 'כוללים'} sub={IS_OPEN_GEMARA ? undefined : 'כולל לכל סדר — הכולל הראשון שמולא הוא הכולל הראשי'} icon="building">
          <div className="nx-stack">
            {kolelLoadError ? <Banner tone="warning" icon="alert">{kolelLoadError}</Banner> : null}
            <FormGrid cols={3}>
              {IS_OPEN_GEMARA
                ? kolelPicker('כולל', 'kolel', 'kolelRef', kolelOptions, findKolel(form.kolelRef) || findKolel(form.kolelMorningRef))
                : [
                  kolelPicker('כולל לפנה"צ', 'kolelMorning', 'kolelMorningRef', kolelsForSlot('morning', form.kolelMorningRef), findKolel(form.kolelMorningRef)),
                  kolelPicker('כולל אחה"צ', 'kolelAfternoon', 'kolelAfternoonRef', kolelsForSlot('afternoon', form.kolelAfternoonRef), findKolel(form.kolelAfternoonRef)),
                  kolelPicker('כולל ערב', 'kolelEvening', 'kolelEveningRef', kolelsForSlot('evening', form.kolelEveningRef), findKolel(form.kolelEveningRef)),
                ]}
            </FormGrid>
          </div>
        </Panel>
      ) : null}
    </>
  );

  const m = member || {};
  const kolelText = (ref, name) => { const k = findKolel(ref); return k ? kolelLabel(k) : name || ''; };
  const detailSections = [
    {
      key: 'personal',
      title: 'פרטים אישיים',
      fields: [
        ...PERSONAL.map(([key, label, ltr]) => ({ key, label, value: m[key], ltr: !!ltr, copy: !!ltr })),
        { key: 'personalCode', label: 'קוד אישי', value: m.personalCode != null ? String(m.personalCode).padStart(4, '0') : '', ltr: true, copy: true },
        { key: 'occupation', label: 'עיסוק', value: m.occupation },
        ...(isWorking(m.occupation) ? [
          { key: 'studyFormat', label: 'חברותא / שיעור', value: m.studyFormat },
          ...(m.studyFormat ? [{ key: 'studyPartner', label: partnerLabel(m.studyFormat), value: m.studyPartner }] : []),
        ] : []),
      ],
    },
    {
      key: 'address',
      title: 'כתובת',
      fields: ADDRESS.map(([key, label]) => ({ key, label, value: m[key] })),
    },
    ...(!IS_MISHTAMERET ? [{
      key: 'kolels',
      title: IS_OPEN_GEMARA ? 'כולל' : 'כוללים',
      fields: IS_OPEN_GEMARA
        ? [{ key: 'kolel', label: 'כולל', value: kolelText(m.kolelRef || m.kolelMorningRef, m.kolel) }]
        : [
          { key: 'kolelMorning', label: 'לפנה"צ', value: kolelText(m.kolelMorningRef, m.kolelMorning) },
          { key: 'kolelAfternoon', label: 'אחה"צ', value: kolelText(m.kolelAfternoonRef, m.kolelAfternoon) },
          { key: 'kolelEvening', label: 'ערב', value: kolelText(m.kolelEveningRef, m.kolelEvening) },
        ],
    }] : []),
  ];
  const openEdit = () => { setForm(member); setError(''); setEditOpen(true); };
  const closeEdit = () => { if (saving) return; setEditOpen(false); setForm(member); setError(''); };

  return (
    <div className="nx-stack">
      {!recordId ? <button type="button" className="adm-back" onClick={back}><span aria-hidden="true">→</span>חברים</button> : null}
      <PageHeader
        title={isNew ? 'חבר חדש' : fullName || 'חבר'}
        sub={isNew ? 'כרטיס חבר' : [member?.phone, member?.city, member?.idNumber ? `ת.ז. ${member.idNumber}` : null, member?.personalCode != null ? `קוד אישי ${String(member.personalCode).padStart(4, '0')}` : null].filter(Boolean).join(' · ')}
        primary={isNew ? { label: saving ? 'שומר…' : 'צור חבר', icon: 'check', onClick: save } : { label: 'עריכה', icon: 'edit', onClick: openEdit }}
      >
        <Button variant="ghost" onClick={back}>{recordId ? 'סגירה' : 'חזרה'}</Button>
      </PageHeader>
      {error && !editOpen ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}

      {isNew ? formPanels : (
        <div className="nx-record">
          <div className="ck-panel nx-record__details">
            {detailSections.map((sec) => <FieldSection key={sec.key} section={sec} onCopied={() => toast.success('הועתק')} />)}
          </div>
          <div className="nx-stack nx-record__main">
            <Tabs
              value={tab}
              onChange={setTab}
              ariaLabel="כרטיס חבר"
              items={[
                { value: 'feed', label: 'יומן פעילות' },
                canReadRegs && !IS_MISHTAMERET ? { value: 'regs', label: 'מבחנים' } : null,
                canReadGrades ? { value: 'attempts', label: IS_MISHTAMERET ? 'מבחנים' : 'מבחנים שנבחן', count: attemptCount || null } : null,
                canReadGrades ? { value: 'grades', label: 'ציונים' } : null,
                canReadMessages ? { value: 'messages', label: 'הודעות למערכת', count: inboxCount || null } : null,
                { value: 'pending', label: 'בקשות עדכון', count: pendingItems.length || null },
              ]}
            />

            {tab === 'feed' ? (
              <div className="ck-panel">
                <ActivityFeed items={activityItems} loading={activityLoading} filters={ACTIVITY_FILTERS} emptyText="אין עדיין פעילות לחבר זה" />
              </div>
            ) : null}

          {tab === 'regs' && !isNew && canReadRegs ? (
            <DataTable
              mode="server"
              {...regs.tableProps}
              rowKey="_id"
              selectable={false}
              searchable={false}
              storageKey="bchinot-member-regs"
              exportFilename={`הרשמות_${form.lastName || ''}`}
              onExport={async ({ format, columns }) => {
                const { data } = await api.get('/registrations', { params: { memberId: id, all: 1 } });
                await exportRows(data.data || [], columns, { format, filename: `הרשמות_${form.lastName || ''}` });
              }}
              emptyTitle="אין הרשמות"
              actionsWidth={IS_OPEN_GEMARA ? 130 : 90}
              rowActions={(reg) => (reg.status !== 'registered' ? [] : IS_OPEN_GEMARA ? [{
                key: 'examine',
                node: (() => {
                  const done = (reg.gemaraSelection || []).filter((s) => s.examined).length;
                  const total = (reg.gemaraSelection || []).length;
                  return <Button size="sm" variant={done ? 'primary' : 'default'} icon="check" onClick={() => setRegExamineDialogReg(reg)}>{done ? `${done}/${total}` : 'סמן נבחן'}</Button>;
                })(),
              }] : [{
                key: 'examined',
                node: <Checkbox checked={!!reg.examined} onChange={() => toggleExamined(reg)} label="נבחן" />,
              }])}
              columns={regColumns}
            />
          ) : null}

          {tab === 'attempts' && !isNew && canReadGrades ? (
            <DataTable
              mode="server"
              {...attempts.tableProps}
              rowKey="_id"
              selectable={false}
              searchable={false}
              storageKey="bchinot-member-attempts"
              exportFilename={`מבחנים_${m.lastName || ''}`}
              onExport={async ({ format, columns }) => {
                const { data } = await api.get('/american-exams/attempts', { params: { memberId: id, all: 1 } });
                await exportRows(data.data || [], columns, { format, filename: `מבחנים_${m.lastName || ''}` });
              }}
              emptyTitle="החבר עדיין לא נבחן"
              rowActions={canDeleteAttempts ? (r) => [{ key: 'del', icon: 'trash', label: 'מחיקת המבחן', onClick: () => setDeleteAttempt(r) }] : undefined}
              columns={[
                { key: 'finishedAt', label: 'תאריך ושעה', type: 'date', render: (r) => (r.finishedAt ? formatHebrewDateTime(r.finishedAt) : '—') },
                { key: 'examTitle', label: 'מבחן', sortable: false },
                { key: 'frameLabel', label: 'מסגרת', sortable: false },
                { key: 'score', label: 'ציון', type: 'number', getValue: attemptScore, render: (r) => <Tag tone={Number(r.score) >= 90 ? 'success' : 'neutral'}><b>{attemptScore(r)}</b></Tag> },
                { key: 'correctCount', label: 'נכונות', sortable: false, getValue: (r) => (r.totalQuestions ? `${r.correctCount ?? 0}/${r.totalQuestions}` : '—') },
                {
                  key: 'channel',
                  label: 'ערוץ',
                  sortable: false,
                  getValue: (r) => (ATTEMPT_CHANNEL[r.channel] || ATTEMPT_CHANNEL.web).label,
                  render: (r) => { const c = ATTEMPT_CHANNEL[r.channel] || ATTEMPT_CHANNEL.web; return <Tag tone={c.tone} icon={c.icon}>{c.label}</Tag>; },
                },
              ]}
            />
          ) : null}

          {tab === 'grades' && !isNew && canReadGrades ? (
            <DataTable
              mode="server"
              {...grades.tableProps}
              rowKey="_id"
              selectable={false}
              searchable={false}
              storageKey="bchinot-member-grades"
              exportFilename={`ציונים_${form.lastName || ''}`}
              onExport={async ({ format, columns }) => {
                const { data } = await api.get('/grades', { params: { memberId: id, all: 1 } });
                await exportRows(data.data || [], columns, { format, filename: `ציונים_${form.lastName || ''}` });
              }}
              toolbarEnd={!IS_MISHTAMERET ? <Button size="sm" variant="primary" icon="plus" onClick={() => { setEditingGrade(null); setGradeDialogOpen(true); }}>הוסף ציון</Button> : null}
              onRowClick={!IS_MISHTAMERET ? (g) => { setEditingGrade(g); setGradeDialogOpen(true); } : undefined}
              rowActions={!IS_MISHTAMERET ? (g) => [{ key: 'edit', icon: 'edit', label: 'עריכה', onClick: () => { setEditingGrade(g); setGradeDialogOpen(true); } }] : undefined}
              emptyTitle="אין ציונים"
              columns={gradeColumns}
            />
          ) : null}

          {tab === 'messages' && !isNew && canReadMessages ? (
            <DataTable
              mode="server"
              {...inbox.tableProps}
              rowKey="_id"
              selectable={false}
              searchable={false}
              storageKey="bchinot-member-inbox"
              exportFilename={`הודעות למערכת_${m.lastName || ''}`}
              onExport={async ({ format, columns }) => {
                const { data } = await api.get('/messages', { params: { inbox: 'ivr', memberId: id, all: 1 } });
                await exportRows(data.data || [], columns, { format, filename: `הודעות למערכת_${m.lastName || ''}` });
              }}
              toolbarEnd={inbox.total ? <Button size="sm" icon="download" disabled={inboxDownloading} onClick={downloadMemberInbox}>{inboxDownloading ? 'מכין ZIP…' : 'הורדת ההקלטות'}</Button> : null}
              emptyTitle="אין הודעות מהטלפונים של החבר"
              rowMenu={canUpdateMessages ? (r) => Object.entries(INBOX_STATUS).filter(([v]) => v !== (r.inboxStatus || 'new')).map(([v, st]) => ({
                key: v, label: `סמן «${st.label}»`, icon: st.icon, onClick: () => setInboxStatus(r, v),
              })) : undefined}
              columns={[
                { key: 'inboxStatus', label: 'סטטוס', sortable: false, filterable: false, getValue: (r) => INBOX_STATUS[r.inboxStatus || 'new'].label, render: (r) => { const st = INBOX_STATUS[r.inboxStatus || 'new']; return <Tag tone={st.tone} icon={st.icon}>{st.label}</Tag>; } },
                { key: 'createdAt', label: 'תאריך', type: 'date', render: (r) => (r.createdAt ? formatHebrewDateTime(r.createdAt) : '—') },
                { key: 'createdBy', label: 'מטלפון', ltr: true, getValue: (r) => callerOf(r) || '—' },
                { key: 'title', label: 'סוג', getValue: inboxKind, render: (r) => <Tag tone={inboxKind(r) === 'להנהלה' ? 'violet' : 'info'}>{inboxKind(r)}</Tag> },
                {
                  key: 'audio',
                  label: 'השמעה',
                  sortable: false,
                  filterable: false,
                  exportable: false,
                  width: 260,
                  render: (r) => (r.audioUrl || String(r.body || '').trim()
                    ? <span onClick={(e) => e.stopPropagation()} role="presentation"><AudioPlayer src={`/messages/${r._id}/audio`} label="הודעה למערכת" onFirstPlay={() => { if ((r.inboxStatus || 'new') === 'new') setInboxRowStatus(r._id, 'played'); }} /></span>
                    : <span className="ck-muted">אין הקלטה</span>),
                },
                { key: 'downloadedAt', label: 'הורד', type: 'date', render: (r) => (r.downloadedAt ? formatHebrewDateTime(r.downloadedAt) : <span className="ck-muted">טרם הורד</span>) },
              ]}
            />
          ) : null}

          {tab === 'pending' && !isNew ? (
            <Panel title="בקשות עדכון פרטים — ממתינות לאישור" sub="הוקלטו בקו הטלפוני" icon="mic" tone="warning">
              {pendingItems.length === 0 ? <EmptyState compact icon="check" title="אין בקשות ממתינות" /> : (
                <div className="nx-list">
                  {pendingItems.map((item) => (
                    <div key={item._id} className="nx-item">
                      <div className="nx-item__main">
                        <b>{FIELD_LABEL[item.field] || item.label || item.field}</b>
                        <small>{item.requestedAt ? formatHebrewDateTime(item.requestedAt) : '—'}</small>
                      </div>
                      {item.recordingUrl ? <AudioPlayer path={item.recordingUrl} label="הקלטת הבקשה" /> : <span className="ck-muted">אין הקלטה</span>}
                      <div className="nx-item__acts">
                        <Button size="sm" variant="primary" icon="check" onClick={() => { setApproveTarget(item); setApproveValue(''); }}>אשר ועדכן</Button>
                        <IconButton icon="x" label="דחייה" onClick={() => setRejectTarget(item)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          ) : null}
          </div>
        </div>
      )}

      <Dialog
        open={editOpen}
        onClose={closeEdit}
        title={`עריכת פרטי ${fullName || 'חבר'}`}
        icon="edit"
        size="lg"
        footer={(
          <>
            <Button variant="ghost" disabled={saving} onClick={closeEdit}>ביטול</Button>
            <Button variant="primary" icon="check" disabled={saving} onClick={save}>{saving ? 'שומר…' : 'שמירה'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
          {formPanels}
        </div>
      </Dialog>

      <Dialog
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        title="אישור עדכון פרטים"
        icon="check"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={approveLoading} onClick={() => setApproveTarget(null)}>ביטול</Button>
            <Button variant="primary" disabled={approveLoading} onClick={approve}>{approveLoading ? 'שומר…' : 'אשר ועדכן'}</Button>
          </>
        )}
      >
        {approveTarget ? (
          <div className="nx-stack">
            <p className="nx-desc">שדה: <b>{FIELD_LABEL[approveTarget.field] || approveTarget.label}</b></p>
            {approveTarget.recordingUrl ? <AudioPlayer path={approveTarget.recordingUrl} label="האזן להקלטה" compact={false} /> : null}
            <Field label="ערך חדש"><TextInput value={approveValue} onChange={(e) => setApproveValue(e.target.value)} placeholder="הקלידו את הערך לעדכון" data-autofocus onKeyDown={(e) => { if (e.key === 'Enter') approve(); }} /></Field>
          </div>
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={reject}
        title="דחיית בקשה"
        message={<p className="ck-confirm__msg">{`לדחות את בקשת העדכון לשדה «${FIELD_LABEL[rejectTarget?.field] || rejectTarget?.label || ''}»?`}</p>}
        confirmLabel="דחה"
      />

      <ConfirmDialog
        open={!!deleteAttempt}
        onClose={() => !deletingAttempt && setDeleteAttempt(null)}
        onConfirm={confirmDeleteAttempt}
        busy={deletingAttempt}
        danger
        title="מחיקת מבחן"
        confirmLabel="מחק"
        message={deleteAttempt ? `למחוק את המבחן «${deleteAttempt.examTitle || 'מבחן'}»${deleteAttempt.maxScore != null ? ` (ציון ${attemptScore(deleteAttempt)})` : ''}? הפעולה תמחק את הרשומה לצמיתות ותאפשר להיבחן שוב על אותו מבחן. לא ניתן לשחזור.` : ''}
      />

      {IS_OPEN_GEMARA ? (
        <GemaraExamineEditDialog
          key={regExamineDialogReg?._id || 'examine'}
          open={!!regExamineDialogReg}
          memberName={fullName}
          initialSelection={regExamineDialogReg?.gemaraSelection || []}
          onSave={saveGemaraExamine}
          saving={regExamineSaving}
          onClose={() => !regExamineSaving && setRegExamineDialogReg(null)}
        />
      ) : null}
      {canReadGrades ? (
        <GradeFormDialog
          open={gradeDialogOpen}
          onClose={() => { setGradeDialogOpen(false); setEditingGrade(null); }}
          member={!isNew ? { _id: id, firstName: form.firstName, lastName: form.lastName, personalCode: form.personalCode } : null}
          grade={editingGrade}
          onSaved={() => grades.reload()}
        />
      ) : null}
    </div>
  );
}
